import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from "react-icons/fa";

interface TranscriptPlayerProps {
    audioUrl: string;
    transcript: string;
    /** Kept for API compatibility; deliberately not rendered (noise to clinicians). */
    filename?: string;
    onAudioError?: () => void;
}

const TranscriptPlayer: React.FC<TranscriptPlayerProps> = ({
    audioUrl,
    transcript,
    onAudioError,
}) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [audioFailed, setAudioFailed] = useState(false);

    // Update current time
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // Browser MediaRecorder webm files carry no duration in their metadata,
        // so audio.duration reads Infinity and the UI shows "Infinity:NaN".
        // Seeking far past the end forces the browser to decode to the real
        // end and emit a durationchange with the true value; we then rewind.
        let probingDuration = false;

        const resolveDuration = () => {
            if (audio.duration === Infinity) {
                if (probingDuration) return;
                probingDuration = true;
                const onDurationChange = () => {
                    if (audio.duration !== Infinity) {
                        setDuration(audio.duration);
                        audio.removeEventListener("durationchange", onDurationChange);
                        audio.currentTime = 0;
                        probingDuration = false;
                    }
                };
                audio.addEventListener("durationchange", onDurationChange);
                audio.currentTime = 1e101;
                return;
            }
            if (!isNaN(audio.duration)) setDuration(audio.duration);
        };

        // Ignore the synthetic timestamps produced while probing the duration.
        const updateTime = () => {
            if (probingDuration) return;
            setCurrentTime(audio.currentTime);
        };
        const handleEnded = () => setIsPlaying(false);
        const handleError = () => {
            setAudioFailed(true);
            setIsPlaying(false);
        };

        audio.addEventListener("timeupdate", updateTime);
        audio.addEventListener("loadedmetadata", resolveDuration);
        audio.addEventListener("ended", handleEnded);
        audio.addEventListener("error", handleError);

        // Metadata may already be loaded before this effect attaches.
        if (audio.readyState >= 1) resolveDuration();

        return () => {
            audio.removeEventListener("timeupdate", updateTime);
            audio.removeEventListener("loadedmetadata", resolveDuration);
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("error", handleError);
        };
    }, []);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;

        const newTime = parseFloat(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;

        const newVolume = parseFloat(e.target.value);
        audio.volume = newVolume;
        setVolume(newVolume);
        setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isMuted) {
            audio.volume = volume || 0.5;
            setIsMuted(false);
            setVolume(volume || 0.5);
        } else {
            audio.volume = 0;
            setIsMuted(true);
        }
    };

    const formatTime = (time: number) => {
        // Guard Infinity too — webm durations read Infinity until resolved.
        if (!isFinite(time) || isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    const hasDuration = isFinite(duration) && duration > 0;
    const progressPct = hasDuration
        ? Math.min(100, (currentTime / duration) * 100)
        : 0;

    if (audioFailed) {
        return (
            <div className="bg-red-50 rounded-lg p-3 border border-red-200 shadow-md flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div className="flex-1">
                    <p className="text-sm font-medium text-red-700">Audio unavailable</p>
                    <p className="text-xs text-red-500">The playback link may have expired.</p>
                </div>
                {onAudioError && (
                    <button
                        onClick={onAudioError}
                        className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                        Refresh
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200 shadow-md">
            {/* Header — the generated filename is noise to clinicians, so it is
                intentionally not displayed. */}
            <div className="mb-2">
                <h3 className="text-sm font-semibold text-gray-800">
                    Audio Recording
                </h3>
            </div>

            {/* Audio Element (hidden) */}
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            {/* Custom Controls */}
            <div className="bg-white rounded-lg p-2 shadow-sm">
                {/* Play/Pause Button and Time Display */}
                <div className="flex items-center gap-2 mb-1">
                    <button
                        onClick={togglePlayPause}
                        className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white flex items-center justify-center shadow-md transition-all transform hover:scale-105 active:scale-95"
                    >
                        {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
                    </button>

                    <div className="flex-1">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                        </div>

                        {/* Progress Bar */}
                        <input
                            type="range"
                            min="0"
                            max={hasDuration ? duration : 0}
                            value={currentTime}
                            onChange={handleSeek}
                            disabled={!hasDuration}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:cursor-default"
                            style={{
                                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${progressPct}%, #e5e7eb ${progressPct}%, #e5e7eb 100%)`,
                            }}
                        />
                    </div>

                    {/* Volume Control */}
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={toggleMute}
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            {isMuted ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
                        </button>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            className="w-16 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TranscriptPlayer;
